import CloseModalButton from "../../buttons/CloseModalButton"
import styles from "./styles.module.scss"
import cardValidator from "card-validator"
import getCreditCardType, { getTypeInfo as getCreditCardTypeInfo } from "credit-card-type"
import ReactCardFlip from "react-card-flip"
import {
  CreditCardTypeCardBrandId,
  CreditCardType,
  getInternalCardType,
  TokenizedCreditCard,
  maskCardNumber,
  UserID,
} from "@cruisen/contract"
import { makeAutoObservable, toJS } from "mobx"
import { useMemo } from "react"
import { colors } from "@theme/colors"
import { DefaultInput } from "../../atoms/DefaultInput"
import { observer } from "mobx-react-lite"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { saveUserCreditCard } from "../../../services/firebase-functions/billing"
import classNames from "classnames"
import VisaIcon from "../../assets/icons/creditCardTypes/VisaIcon"
import AMXIcon from "../../assets/icons/creditCardTypes/AMXIcon"
import MasterCardIcon from "../../assets/icons/creditCardTypes/MasterCardIcon"

type CreditCardDetails = {
  cardType: CreditCardType | ""
  cardBrandId: CreditCardTypeCardBrandId | ""
  cardholderName: string
  cardNumber: string
  expiryDate: string
  signature: string
}

const toStatus = (validation: { isValid?: boolean; isPotentiallyValid?: boolean }) => {
  return validation.isValid ? "valid" : validation.isPotentiallyValid ? "incomplete" : "invalid"
}

const defaultCardNumberLength = 16
const defaultCardNumberGaps = [4, 8, 12]
const defaultCVCValidation = { code: { size: 3 } }

const validateCreditCardDetails = (
  creditCardDetails: CreditCardDetails,
  creditCardTokenId?: string,
) => {
  const numberValidation = cardValidator.number(creditCardDetails.cardNumber)
  const expiryValidation = cardValidator.expirationDate(creditCardDetails.expiryDate)

  const cardTypeData = getCreditCardTypeInfo(creditCardDetails.cardBrandId)
  const maxCVCLength = (cardTypeData || defaultCVCValidation).code.size
  const cvcValidation = cardValidator.cvv(creditCardDetails.signature, maxCVCLength)

  const result = {
    cardNumber: creditCardTokenId ? "valid" : toStatus(numberValidation),
    expiryDate: creditCardTokenId ? "valid" : toStatus(expiryValidation),
    signature: toStatus(cvcValidation),
    cardholderName: creditCardTokenId
      ? "valid"
      : creditCardDetails.cardholderName.length === 0
        ? "incomplete"
        : "valid",
  } as const

  return {
    valid: result,
    maxCVCLength,
    status: Object.values(result).every((status) => status === "valid") ? "valid" : "invalid",
  }
}

class CreditCardModalStore {
  creditCardDetails: CreditCardDetails
  creditCardTokenId: string
  focusedInput: string

  constructor(cardDetails: TokenizedCreditCard | null) {
    if (cardDetails) {
      const cardTypeData = getCreditCardTypeInfo(cardDetails.brandId)

      this.creditCardDetails = {
        cardType: cardDetails.cardType,
        cardBrandId: cardDetails.brandId,
        cardholderName: cardDetails.cardholderName,
        cardNumber: maskCardNumber(
          cardDetails.cardBin,
          cardDetails.cardLast4,
          cardTypeData?.lengths?.[0] ?? defaultCardNumberLength,
          cardTypeData?.gaps ?? defaultCardNumberGaps,
        ),
        expiryDate: `${cardDetails.expiryMonth}/${cardDetails.expiryYear.toString().slice(-2)}`,
        signature: "",
      }

      this.creditCardTokenId = cardDetails.tokenId
    } else {
      this.creditCardDetails = {
        cardType: "",
        cardBrandId: "",
        cardholderName: "",
        cardNumber: "",
        expiryDate: "",
        signature: "",
      }

      this.creditCardTokenId = ""
    }

    this.focusedInput = ""

    makeAutoObservable(this)
  }

  setProperty<T extends keyof CreditCardDetails>(key: T, value: CreditCardDetails[T]) {
    this.creditCardDetails[key] = value

    if (key === "cardNumber") {
      this.creditCardTokenId = ""

      const cardTypeData = getCreditCardType(value)?.[0]
      const cardBrandId = cardTypeData?.type as CreditCardTypeCardBrandId

      this.creditCardDetails.cardType = getInternalCardType(cardBrandId)
      this.creditCardDetails.cardBrandId = cardBrandId
    }
  }

  setFocusedInput(val: string) {
    this.focusedInput = val
  }
}

const creditCardTypeLogo = {
  AMX: <AMXIcon />,
  MAE: <MasterCardIcon />,
  MSC: <MasterCardIcon />,
  VIS: <VisaIcon />,
}

export const CreditCardModal = observer(
  ({
    onClose,
    onSubmit,
    defaultData,
    loggedInUserId,
  }: {
    onClose: () => void
    onSubmit: (data: TokenizedCreditCard) => void
    defaultData: TokenizedCreditCard | null
    loggedInUserId: UserID
  }) => {
    const store = useMemo(() => new CreditCardModalStore(defaultData), [])
    const creditCardDetails = toJS(store.creditCardDetails)
    const formValidation = validateCreditCardDetails(creditCardDetails, store.creditCardTokenId)
    const queryClient = useQueryClient()

    const { mutateAsync: mutateSaveCreditCard, isPending: isSavingCreditCard } = useMutation({
      mutationFn: saveUserCreditCard,
      onSuccess: () => {
        queryClient
          .invalidateQueries({ queryKey: ["creditCardDetails", loggedInUserId] })
          .catch((error) => console.error("credit card query invalidation failure", error))
        queryClient
          .invalidateQueries({ queryKey: ["creditCards", loggedInUserId] })
          .catch((error) => console.error("credit card query invalidation failure", error))
      },
    })

    const cardType =
      store.creditCardDetails.cardBrandId &&
      getInternalCardType(store.creditCardDetails.cardBrandId)

    const showCardType = cardType && store.creditCardDetails.cardNumber.length > 4

    return (
      <div className={styles.container}>
        <div className={styles.containerHeading}>
          <h2 className={styles.containerTitle}>Payment Method</h2>
          <CloseModalButton onClick={onClose} />
        </div>
        <div className={styles.cardDetailsContainer}>
          <ReactCardFlip isFlipped={store.focusedInput === "signature"}>
            <div className={classNames(styles.creditCard, styles.defaultGradient)}>
              <div className={styles.frontColumn}>
                <div className={styles.creditCardLogo}>
                  {(showCardType && creditCardTypeLogo[cardType]) || null}
                </div>
                <div />
                <div className={styles.bottomFrontColumn}>
                  <p className={styles.boldText}>{store.creditCardDetails.cardNumber}</p>
                  <div className={styles.frontRow}>
                    <p className={styles.regularText}>{store.creditCardDetails.cardholderName}</p>
                    <p className={styles.regularText}>{store.creditCardDetails.expiryDate}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={classNames(styles.creditCard, styles.reversedGradient)}>
              <div className={styles.blackLine} />
              <div className={styles.cvvBox}>
                <p className={styles.regularText}>{store.creditCardDetails.signature}</p>
              </div>
            </div>
          </ReactCardFlip>
          <DefaultInput
            name="cardholderName"
            label="Cardholder name"
            value={store.creditCardDetails.cardholderName}
            parentBackgroundColor={colors.backgroundSecondary}
            onChange={(value) => store.setProperty("cardholderName", value)}
            onFocus={() => store.setFocusedInput("cardholderName")}
            onBlur={() => store.setFocusedInput("")}
          />
          <DefaultInput
            name="cardNumber"
            label="Card number"
            value={store.creditCardDetails.cardNumber}
            parentBackgroundColor={colors.backgroundSecondary}
            maskValue={cardType === "AMX" ? "#### ###### #####" : "#### #### #### ####"}
            onChange={(value) => store.setProperty("cardNumber", value)}
            inputColor={
              formValidation.valid.cardNumber === "invalid" ? colors.textError : colors.textPrimary
            }
            inputMode="numeric"
            onFocus={() => store.setFocusedInput("cardNumber")}
            onBlur={() => store.setFocusedInput("")}
          />
          <DefaultInput
            name="expiryDate"
            label="Expiry"
            value={store.creditCardDetails.expiryDate}
            parentBackgroundColor={colors.backgroundSecondary}
            maskValue="##/##"
            onChange={(value) => store.setProperty("expiryDate", value)}
            inputColor={
              formValidation.valid.expiryDate === "invalid" ? colors.textError : colors.textPrimary
            }
            inputMode="numeric"
            onFocus={() => store.setFocusedInput("expiryDate")}
            onBlur={() => store.setFocusedInput("")}
          />
          <DefaultInput
            name="signature"
            label="CVC code"
            value={store.creditCardDetails.signature}
            parentBackgroundColor={colors.backgroundSecondary}
            maxLength={formValidation?.maxCVCLength || 3}
            onChange={(value) => store.setProperty("signature", value)}
            inputMode="numeric"
            onFocus={() => store.setFocusedInput("signature")}
            onBlur={() => store.setFocusedInput("")}
          />
          <button
            disabled={formValidation.status === "invalid" || isSavingCreditCard}
            className={styles.submitButton}
            onClick={async () => {
              if (formValidation.status === "invalid") return

              const [expiryMonth, expiryYear] = store.creditCardDetails.expiryDate.split("/")

              const tokenizedCreditCard = await mutateSaveCreditCard({
                userId: loggedInUserId,
                tokenId: store.creditCardTokenId,
                creditCard: {
                  cardholderName: store.creditCardDetails.cardholderName,
                  cardType: store.creditCardDetails.cardType as CreditCardType,
                  cardNumber: store.creditCardDetails.cardNumber.replace(/\s+/g, ""),
                  expiryMonth: Number(expiryMonth),
                  expiryYear: Number(`20${expiryYear}`),
                  signature: Number(store.creditCardDetails.signature),
                },
              })

              onSubmit(tokenizedCreditCard)
            }}
          >
            {isSavingCreditCard ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    )
  },
)
